<template>
  <AppLayout>
    <div class="p-4 md:p-6">

      <!-- Header (desktop only) -->
      <div class="hidden md:flex items-center justify-between mb-2">
        <div>
          <h1 class="text-2xl font-bold text-slate-800 dark:text-white">Assets</h1>
          <p class="text-slate-500 dark:text-slate-400 text-sm mt-0.5">
            <span v-if="dataLoading" class="inline-block w-28 h-3 bg-slate-200 dark:bg-slate-700 rounded-full animate-pulse"></span>
            <span v-else>{{ filtered.length }} of {{ assets.length }} asset{{ assets.length !== 1 ? 's' : '' }}</span>
          </p>
        </div>
        <button @click="openAdd"
          class="flex items-center gap-2 bg-indigo-500 hover:bg-indigo-400 active:scale-95 text-white
                 text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-indigo-500/30 transition">
          <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
          </svg>
          <span class="hidden sm:inline">Add Asset</span>
          <span class="sm:hidden">Add</span>
        </button>
      </div>

      <!-- ── MOBILE HEADER + STATS + SEARCH ── -->
      <div class="md:hidden -mx-4 -mt-4 mb-1">
        <!-- Indigo gradient hero -->
        <div class="bg-gradient-to-br from-indigo-600 via-indigo-500 to-violet-500 px-4 pt-3 pb-6">
          <div class="flex items-center justify-between mb-3">
            <div>
              <h1 class="text-xl font-bold text-white">Assets</h1>
              <p class="text-indigo-200 text-xs mt-0.5">
                <span v-if="dataLoading">Loading...</span>
                <span v-else>{{ filtered.length }} of {{ assets.length }} asset{{ assets.length !== 1 ? 's' : '' }}</span>
              </p>
            </div>
            <button @click="openAdd"
              class="flex items-center gap-1.5 bg-white/20 active:bg-white/30 text-white text-sm font-semibold px-4 py-2.5 rounded-xl transition">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                <path stroke-linecap="round" stroke-linejoin="round" d="M12 4.5v15m7.5-7.5h-15"/>
              </svg>
              Add
            </button>
          </div>
          <!-- Stats row -->
          <div v-if="!dataLoading" class="grid grid-cols-3 gap-2 mb-2">
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-emerald-200 uppercase tracking-widest mb-1">Total Value</p>
              <p class="text-[10px] min-[360px]:text-xs min-[400px]:text-sm font-bold text-white whitespace-nowrap">Rs {{ formatNum(totalValue) }}</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-blue-200 uppercase tracking-widest mb-1">Owned Value</p>
              <p class="text-[10px] min-[360px]:text-xs min-[400px]:text-sm font-bold text-white whitespace-nowrap">Rs {{ formatNum(totalOwnedValue) }}</p>
            </div>
            <div class="bg-white/15 backdrop-blur-sm rounded-2xl px-3 py-3 text-center">
              <p class="text-[9px] font-bold text-yellow-200 uppercase tracking-widest mb-1">Assets</p>
              <p class="text-[10px] min-[360px]:text-xs min-[400px]:text-sm font-bold text-white whitespace-nowrap">{{ assets.length }}</p>
            </div>
          </div>
          <!-- Stats skeleton -->
          <div v-else class="grid grid-cols-3 gap-2 mb-2">
            <div v-for="i in 3" :key="i" class="bg-white/15 rounded-2xl h-14 animate-pulse"></div>
          </div>
        </div>

        <!-- Search card pulls up over hero -->
        <div class="bg-gray-50 dark:bg-slate-950 rounded-t-3xl -mt-4 px-4 pt-3 pb-2">
          <!-- Search input -->
          <div class="relative mb-2">
            <svg class="absolute left-3.5 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400 pointer-events-none"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
            </svg>
            <input v-model="search" type="text" placeholder="Search assets..."
              class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-2xl pl-10 pr-10 py-3 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 shadow-sm transition" />
            <button v-if="search" @click="search = ''"
              class="absolute right-3.5 top-1/2 -translate-y-1/2 text-slate-400">
              <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
              </svg>
            </button>
          </div>
        </div>
      </div>

      <!-- Search (desktop only) -->
      <div class="hidden md:flex items-center gap-3 mb-3">
        <!-- Search -->
        <div class="relative w-56">
          <svg class="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400 pointer-events-none"
            fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
            <path stroke-linecap="round" stroke-linejoin="round" d="M21 21l-5.197-5.197m0 0A7.5 7.5 0 1 0 5.196 5.196a7.5 7.5 0 0 0 10.607 10.607z"/>
          </svg>
          <input v-model="search" type="text" placeholder="Search..."
            class="w-full bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-700
                   text-slate-800 dark:text-white placeholder-slate-400 rounded-xl pl-9 pr-8 py-1.5 text-sm
                   focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition" />
          <button v-if="search" @click="search = ''"
            class="absolute right-2.5 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600 dark:hover:text-slate-500 dark:text-slate-300 transition">
            <svg class="w-3.5 h-3.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>

        <!-- Spacer -->
        <div class="flex-1"></div>

        <!-- Stats -->
        <div class="hidden md:flex items-center gap-2">
          <template v-if="dataLoading">
            <div class="h-10 w-32 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
            <div class="h-10 w-32 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
            <div class="h-10 w-28 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse"></div>
          </template>
          <template v-else>
            <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
              <p class="text-[10px] font-semibold text-emerald-500 uppercase tracking-wide">Total Value</p>
              <p class="text-sm font-bold text-emerald-600 dark:text-emerald-400">Rs {{ formatNum(totalValue) }}</p>
            </div>
            <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
              <p class="text-[10px] font-semibold text-blue-500 uppercase tracking-wide">Owned Value</p>
              <p class="text-sm font-bold text-blue-600 dark:text-blue-400">Rs {{ formatNum(totalOwnedValue) }}</p>
            </div>
            <div class="bg-white dark:bg-slate-900 border border-slate-100 dark:border-slate-800 rounded-xl px-4 py-2 text-right">
              <p class="text-[10px] font-semibold text-yellow-500 uppercase tracking-wide">Count</p>
              <p class="text-sm font-bold text-yellow-600 dark:text-yellow-400">{{ assets.length }}</p>
            </div>
          </template>
        </div>
      </div>

      <!-- Flash -->
      <div v-if="$page.props.flash?.notice"
        class="mb-4 flex items-center gap-2 bg-green-50 dark:bg-green-950 text-green-700 dark:text-green-400 text-sm px-4 py-3 rounded-xl">
        <svg class="w-4 h-4 shrink-0" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
          <path stroke-linecap="round" stroke-linejoin="round" d="M9 12.75L11.25 15 15 9.75M21 12a9 9 0 11-18 0 9 9 0 0118 0z"/>
        </svg>
        {{ $page.props.flash.notice }}
      </div>
      <div v-if="$page.props.flash?.alert || errors?.length"
        class="mb-4 bg-red-50 dark:bg-red-950 text-red-600 dark:text-red-400 text-sm px-4 py-3 rounded-xl">
        <p v-if="$page.props.flash?.alert">• {{ $page.props.flash.alert }}</p>
        <p v-for="e in (errors || [])" :key="e">• {{ e }}</p>
      </div>

      <!-- Skeleton -->
      <template v-if="dataLoading">
        <!-- Desktop skeleton -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div class="border-b border-slate-100 dark:border-slate-800 px-5 py-3.5 flex gap-8">
            <div v-for="i in 5" :key="i" class="h-2.5 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
          </div>
          <div v-for="i in 7" :key="i" class="flex items-center gap-6 px-5 py-4 border-b border-slate-50 dark:border-slate-800/50">
            <div class="flex-1 space-y-1.5">
              <div class="h-3 w-32 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              <div class="h-2.5 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            </div>
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse ml-auto"></div>
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-3 w-20 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
            <div class="h-7 w-14 bg-slate-100 dark:bg-slate-800 rounded-lg animate-pulse"></div>
          </div>
        </div>
        <!-- Mobile skeleton -->
        <div class="md:hidden space-y-2.5">
          <div v-for="i in 5" :key="i"
            class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
            <div class="flex items-center gap-3 px-4 py-3.5">
              <div class="flex-1 space-y-2">
                <div class="h-3 w-32 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
                <div class="h-2.5 w-24 bg-slate-100 dark:bg-slate-800 rounded-full animate-pulse"></div>
              </div>
              <div class="h-8 w-16 bg-slate-100 dark:bg-slate-800 rounded-xl animate-pulse shrink-0"></div>
            </div>
          </div>
        </div>
      </template>

      <!-- Empty state -->
      <div v-else-if="filtered.length === 0"
        class="flex flex-col items-center justify-center py-20 text-center">
        <div class="w-16 h-16 rounded-2xl bg-slate-100 dark:bg-slate-800 flex items-center justify-center mb-4">
          <svg class="w-8 h-8 text-slate-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="1.5">
            <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 21h19.5m-18-18v18m10.5-18v18m6-13.5V21M6.75 6.75h.75m-.75 3h.75m-.75 3h.75m3-6h.75m-.75 3h.75m-.75 3h.75M6.75 21v-3.375c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21M3 3h12m-.75 4.5H21m-3.75 3.75h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008zm0 3h.008v.008h-.008v-.008z"/>
          </svg>
        </div>
        <p class="text-slate-600 dark:text-slate-400 font-medium">
          {{ search ? 'No assets match your search' : 'No assets yet' }}
        </p>
        <p class="text-slate-400 dark:text-slate-500 text-sm mt-1">
          {{ search ? 'Try a different keyword' : 'Add your first asset to track your wealth' }}
        </p>
        <button v-if="!search" @click="openAdd" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">+ Add Asset</button>
        <button v-else @click="search = ''" class="mt-4 text-indigo-500 text-sm font-semibold hover:text-indigo-400 transition">Clear search</button>
      </div>

      <!-- Data loaded and has results -->
      <template v-else>
        <!-- Desktop table -->
        <div class="hidden md:block bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-visible">
          <table class="w-full text-sm border-collapse" style="border-radius: inherit;">
            <thead>
              <tr class="border-b border-slate-100 dark:border-slate-800">
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide w-12"></th>
                <th class="text-left px-5 py-3.5 text-xs font-semibold text-slate-400 uppercase tracking-wide">Asset Name</th>
                <th @click="toggleSort('current_price')" class="px-5 py-3.5 w-40 cursor-pointer select-none">
                  <div class="flex items-center justify-end gap-1 text-xs font-semibold uppercase tracking-wide"
                    :class="sortCol === 'current_price' ? 'text-emerald-500' : 'text-emerald-500/60 hover:text-emerald-500'">
                    Current Value
                    <svg class="w-3 h-3 transition-transform" :class="sortCol === 'current_price' && sortDir === 'asc' ? 'rotate-180' : ''"
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                    </svg>
                  </div>
                </th>
                <th class="px-5 py-3.5 w-32 text-xs font-semibold text-amber-500/60 uppercase tracking-wide text-right">Profit/Loss</th>
                <th @click="toggleSort('annual_return')" class="px-5 py-3.5 w-32 cursor-pointer select-none">
                  <div class="flex items-center justify-end gap-1 text-xs font-semibold uppercase tracking-wide"
                    :class="sortCol === 'annual_return' ? 'text-purple-500' : 'text-purple-500/60 hover:text-purple-500'">
                    Annual Return
                    <svg class="w-3 h-3 transition-transform" :class="sortCol === 'annual_return' && sortDir === 'asc' ? 'rotate-180' : ''"
                      fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2.5">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
                    </svg>
                  </div>
                </th>
                <th class="px-5 py-3.5 w-24 text-xs font-semibold text-slate-400 uppercase tracking-wide text-center">Actions</th>
              </tr>
            </thead>
          <tbody>
            <template v-for="(asset, idx) in sorted" :key="asset.id">
              <!-- Main Row -->
              <tr class="border-b border-slate-50 dark:border-slate-800/50 hover:bg-slate-50 dark:hover:bg-slate-800/30 transition cursor-pointer"
                @click="expandedId = expandedId === asset.id ? null : asset.id">
                <td class="px-5 py-4">
                  <svg class="w-4 h-4 text-slate-400 transition-transform" :class="expandedId === asset.id ? 'rotate-90' : ''"
                    fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5"/>
                  </svg>
                </td>
                <td class="px-5 py-4">
                  <p class="text-slate-800 dark:text-white font-semibold">{{ asset.asset_name }}</p>
                  <p v-if="asset.purchased_at" class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                    Purchased: {{ formatDate(asset.purchased_at) }}
                  </p>
                </td>
                <td class="px-5 py-4 text-right font-semibold text-emerald-600 dark:text-emerald-400">
                  Rs {{ formatNum(asset.current_price) }}
                </td>
                <td class="px-5 py-4 text-right font-semibold"
                  :class="asset.profit_loss >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                  <div class="flex items-center justify-end gap-1">
                    <svg v-if="asset.profit_loss >= 0" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"/>
                    </svg>
                    <svg v-else class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3"/>
                    </svg>
                    <span>{{ asset.profit_loss >= 0 ? '+' : '' }}{{ asset.profit_loss_percentage.toFixed(1) }}%</span>
                  </div>
                </td>
                <td class="px-5 py-4 text-right font-semibold"
                  :class="asset.annual_return >= 0 ? 'text-purple-600 dark:text-purple-400' : 'text-red-600 dark:text-red-400'">
                  <div class="flex items-center justify-end gap-1">
                    <svg v-if="asset.annual_return >= 0" class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"/>
                    </svg>
                    <svg v-else class="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3"/>
                    </svg>
                    <span>{{ asset.annual_return >= 0 ? '+' : '' }}{{ asset.annual_return.toFixed(1) }}%</span>
                  </div>
                </td>
                <td class="px-5 py-4 text-center" @click.stop>
                <div class="relative inline-block">
                  <button @click="toggleMenu(asset.id)"
                    class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 p-1 rounded-lg hover:bg-slate-100 dark:hover:bg-slate-800 transition">
                    <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                      <path stroke-linecap="round" stroke-linejoin="round" d="M12 6.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 12.75a.75.75 0 110-1.5.75.75 0 010 1.5zM12 18.75a.75.75 0 110-1.5.75.75 0 010 1.5z"/>
                    </svg>
                  </button>
                  <div v-if="menuOpenId === asset.id" @click.stop
                    class="absolute right-0 top-full mt-1 w-36 bg-white dark:bg-slate-800 rounded-xl shadow-lg border border-slate-100 dark:border-slate-700 overflow-hidden z-10">
                    <button @click="openEdit(asset)" class="w-full text-left px-4 py-2.5 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-50 dark:hover:bg-slate-700/50 transition flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"/>
                      </svg>
                      Edit
                    </button>
                    <button @click="confirmDelete(asset)" class="w-full text-left px-4 py-2.5 text-sm text-red-600 dark:text-red-400 hover:bg-red-50 dark:hover:bg-red-950/50 transition flex items-center gap-2">
                      <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                        <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                      </svg>
                      Delete
                    </button>
                  </div>
                </div>
              </td>
              </tr>

              <!-- Expanded Details Row -->
              <tr v-if="expandedId === asset.id" class="border-b border-slate-50 dark:border-slate-800/50">
                <td colspan="6" class="px-5 py-4 bg-slate-50 dark:bg-slate-800/30">
                  <div class="grid grid-cols-4 gap-4">
                    <div class="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                      <p class="text-[10px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1.5">Purchased Price</p>
                      <p class="text-sm font-bold text-slate-700 dark:text-slate-300">Rs {{ formatNum(asset.purchased_price) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                      <p class="text-[10px] font-semibold text-emerald-500 uppercase tracking-wide mb-1.5">Current Price</p>
                      <p class="text-sm font-bold text-emerald-600 dark:text-emerald-400">Rs {{ formatNum(asset.current_price) }}</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                      <p class="text-[10px] font-semibold text-indigo-500 uppercase tracking-wide mb-1.5">Ownership</p>
                      <p class="text-sm font-bold text-indigo-600 dark:text-indigo-400">{{ asset.ownership_percentage }}%</p>
                    </div>
                    <div class="bg-white dark:bg-slate-900 rounded-xl p-4 border border-slate-200 dark:border-slate-700">
                      <p class="text-[10px] font-semibold text-blue-500 uppercase tracking-wide mb-1.5">Owned Value</p>
                      <p class="text-sm font-bold text-blue-600 dark:text-blue-400">Rs {{ formatNum(asset.owned_value) }}</p>
                    </div>
                  </div>
                </td>
              </tr>
            </template>
          </tbody>
        </table>
      </div>

      <!-- Mobile list -->
      <div class="md:hidden space-y-2.5">
        <div v-for="asset in sorted" :key="asset.id"
          class="bg-white dark:bg-slate-900 rounded-2xl border border-slate-100 dark:border-slate-800 overflow-hidden">
          <div @click="expandedId = expandedId === asset.id ? null : asset.id"
            class="flex items-center gap-3 px-4 py-3.5 active:bg-slate-50 dark:active:bg-slate-800/50 transition cursor-pointer">
            <div class="flex-1 min-w-0">
              <p class="text-sm font-semibold text-slate-800 dark:text-white truncate">{{ asset.asset_name }}</p>
              <p class="text-xs text-slate-500 dark:text-slate-400 mt-0.5">
                {{ asset.purchased_at ? formatDate(asset.purchased_at) : 'No purchase date' }}
              </p>
            </div>
            <div class="shrink-0 text-right">
              <p class="text-sm font-bold text-blue-600 dark:text-blue-400">Rs {{ formatNum(asset.owned_value) }}</p>
              <p class="text-[10px] text-slate-400 mt-0.5">owned value</p>
            </div>
            <svg class="w-4 h-4 text-slate-400 shrink-0 transition-transform"
              :class="expandedId === asset.id ? 'rotate-180' : ''"
              fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5"/>
            </svg>
          </div>

          <!-- Expanded details -->
          <div v-if="expandedId === asset.id" class="border-t border-slate-100 dark:border-slate-800 bg-slate-50 dark:bg-slate-800/30">
            <div class="grid grid-cols-2 gap-2 p-3">
              <div class="bg-white dark:bg-slate-900 rounded-xl p-2.5 border border-slate-200 dark:border-slate-700">
                <p class="text-[9px] font-semibold text-slate-500 dark:text-slate-400 uppercase tracking-wide mb-1">Purchased</p>
                <p class="text-xs font-bold text-slate-700 dark:text-slate-300">Rs {{ formatNum(asset.purchased_price) }}</p>
              </div>
              <div class="bg-white dark:bg-slate-900 rounded-xl p-2.5 border border-slate-200 dark:border-slate-700">
                <p class="text-[9px] font-semibold text-emerald-500 uppercase tracking-wide mb-1">Current</p>
                <p class="text-xs font-bold text-emerald-600 dark:text-emerald-400">Rs {{ formatNum(asset.current_price) }}</p>
              </div>
              <div class="bg-white dark:bg-slate-900 rounded-xl p-2.5 border border-slate-200 dark:border-slate-700">
                <p class="text-[9px] font-semibold text-amber-500 uppercase tracking-wide mb-1">Profit/Loss</p>
                <p class="text-xs font-bold flex items-center gap-1"
                  :class="asset.profit_loss >= 0 ? 'text-green-600 dark:text-green-400' : 'text-red-600 dark:text-red-400'">
                  <svg v-if="asset.profit_loss >= 0" class="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"/>
                  </svg>
                  <svg v-else class="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3"/>
                  </svg>
                  {{ asset.profit_loss >= 0 ? '+' : '' }}{{ asset.profit_loss_percentage.toFixed(1) }}%
                </p>
              </div>
              <div class="bg-white dark:bg-slate-900 rounded-xl p-2.5 border border-slate-200 dark:border-slate-700">
                <p class="text-[9px] font-semibold text-purple-500 uppercase tracking-wide mb-1">Annual Return</p>
                <p class="text-xs font-bold flex items-center gap-1"
                  :class="asset.annual_return >= 0 ? 'text-purple-600 dark:text-purple-400' : 'text-red-600 dark:text-red-400'">
                  <svg v-if="asset.annual_return >= 0" class="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M4.5 10.5L12 3m0 0l7.5 7.5M12 3v18"/>
                  </svg>
                  <svg v-else class="w-2.5 h-2.5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="3">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M19.5 13.5L12 21m0 0l-7.5-7.5M12 21V3"/>
                  </svg>
                  {{ asset.annual_return >= 0 ? '+' : '' }}{{ asset.annual_return.toFixed(1) }}%
                </p>
              </div>
              <div class="bg-white dark:bg-slate-900 rounded-xl p-2.5 border border-slate-200 dark:border-slate-700">
                <p class="text-[9px] font-semibold text-indigo-500 uppercase tracking-wide mb-1">Ownership</p>
                <p class="text-xs font-bold text-indigo-600 dark:text-indigo-400">{{ asset.ownership_percentage }}%</p>
              </div>
            </div>
            <div class="flex gap-2 p-3 pt-0">
              <button @click="openEdit(asset)"
                class="flex-1 flex items-center justify-center gap-2 bg-white dark:bg-slate-900 text-slate-700 dark:text-slate-300 text-sm font-semibold px-4 py-2.5 rounded-xl border border-slate-200 dark:border-slate-700 active:scale-95 transition">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M16.862 4.487l1.687-1.688a1.875 1.875 0 112.652 2.652L10.582 16.07a4.5 4.5 0 01-1.897 1.13L6 18l.8-2.685a4.5 4.5 0 011.13-1.897l8.932-8.931zm0 0L19.5 7.125M18 14v4.75A2.25 2.25 0 0115.75 21H5.25A2.25 2.25 0 013 18.75V8.25A2.25 2.25 0 015.25 6H10"/>
                </svg>
                Edit
              </button>
              <button @click="confirmDelete(asset)"
                class="flex-1 flex items-center justify-center gap-2 bg-red-50 dark:bg-red-950/50 text-red-600 dark:text-red-400 text-sm font-semibold px-4 py-2.5 rounded-xl border border-red-200 dark:border-red-900 active:scale-95 transition">
                <svg class="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                  <path stroke-linecap="round" stroke-linejoin="round" d="M14.74 9l-.346 9m-4.788 0L9.26 9m9.968-3.21c.342.052.682.107 1.022.166m-1.022-.165L18.16 19.673a2.25 2.25 0 01-2.244 2.077H8.084a2.25 2.25 0 01-2.244-2.077L4.772 5.79m14.456 0a48.108 48.108 0 00-3.478-.397m-12 .562c.34-.059.68-.114 1.022-.165m0 0a48.11 48.11 0 013.478-.397m7.5 0v-.916c0-1.18-.91-2.164-2.09-2.201a51.964 51.964 0 00-3.32 0c-1.18.037-2.09 1.022-2.09 2.201v.916m7.5 0a48.667 48.667 0 00-7.5 0"/>
                </svg>
                Delete
              </button>
            </div>
          </div>
        </div>
      </div>
      </template>

    </div>

    <!-- Add/Edit Modal -->
    <div v-if="modalOpen" @click="closeModal"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div @click.stop class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-md overflow-visible">
        <div class="px-6 py-4 border-b border-slate-100 dark:border-slate-800 flex items-center justify-between rounded-t-2xl overflow-hidden">
          <h2 class="text-lg font-bold text-slate-800 dark:text-white">{{ editingAsset ? 'Edit Asset' : 'Add Asset' }}</h2>
          <button @click="closeModal" class="text-slate-400 hover:text-slate-600 dark:hover:text-slate-300 transition">
            <svg class="w-5 h-5" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M6 18L18 6M6 6l12 12"/>
            </svg>
          </button>
        </div>
        <div class="p-6 space-y-4 overflow-visible">
          <div>
            <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Asset Name *</label>
            <input v-model="form.asset_name" type="text" required
              class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-xl px-4 py-2.5 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
              placeholder="e.g., House, Car, Gold, Stocks" />
          </div>
          <div>
            <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Purchased Price (Rs) *</label>
            <input v-model="form.purchased_price" type="number" required min="1"
              class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-xl px-4 py-2.5 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
              placeholder="e.g., 5000000" />
            <p v-if="purchasedPriceInWords" class="mt-2 text-xs text-slate-500 dark:text-slate-400 italic">
              {{ purchasedPriceInWords }}
            </p>
          </div>
          <div>
            <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Current Price (Rs) *</label>
            <input v-model="form.current_price" type="number" required min="1"
              class="w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-xl px-4 py-2.5 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition"
              placeholder="e.g., 5500000" />
            <p v-if="currentPriceInWords" class="mt-2 text-xs text-slate-500 dark:text-slate-400 italic">
              {{ currentPriceInWords }}
            </p>
          </div>
          <div>
            <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-2.5 flex items-center justify-between">
              <span>Ownership Percentage *</span>
              <span class="text-2xl font-bold text-indigo-600 dark:text-indigo-400">{{ form.ownership_percentage }}%</span>
            </label>

            <!-- Slider track -->
            <div class="relative w-full h-3 bg-slate-200 dark:bg-slate-700 rounded-full overflow-visible">
              <!-- Fill bar -->
              <div class="absolute inset-y-0 left-0 bg-gradient-to-r from-indigo-500 to-indigo-600 dark:from-indigo-400 dark:to-indigo-500 rounded-full transition-all duration-150"
                :style="`width: ${form.ownership_percentage}%`"></div>

              <!-- Draggable circle thumb -->
              <div class="absolute top-1/2 -translate-y-1/2 w-5 h-5 bg-white dark:bg-slate-100 border-2 border-indigo-500 dark:border-indigo-400 rounded-full shadow-lg transition-all duration-150 pointer-events-none"
                :style="`left: calc(${form.ownership_percentage}% - 10px)`">
                <!-- Inner dot for better visibility -->
                <div class="absolute inset-0 m-auto w-2 h-2 bg-indigo-500 dark:bg-indigo-400 rounded-full"></div>
              </div>

              <!-- Range input (invisible but functional) -->
              <input v-model.number="form.ownership_percentage" type="range" min="1" max="100"
                class="absolute inset-0 w-full h-full opacity-0 cursor-pointer z-10" />
            </div>

            <!-- Percentage markers -->
            <div class="flex justify-between mt-1.5 px-0.5">
              <span class="text-xs text-slate-400 dark:text-slate-500">1%</span>
              <span class="text-xs text-slate-400 dark:text-slate-500">25%</span>
              <span class="text-xs text-slate-400 dark:text-slate-500">50%</span>
              <span class="text-xs text-slate-400 dark:text-slate-500">75%</span>
              <span class="text-xs text-slate-400 dark:text-slate-500">100%</span>
            </div>

            <!-- Ownership Value Display -->
            <div v-if="form.current_price && form.current_price > 0" class="mt-3 p-3 bg-blue-50 dark:bg-blue-950/30 border border-blue-100 dark:border-blue-900/50 rounded-xl">
              <div class="flex items-center justify-between mb-2">
                <div class="flex items-center gap-2">
                  <svg class="w-4 h-4 text-blue-500 dark:text-blue-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
                    <path stroke-linecap="round" stroke-linejoin="round" d="M2.25 18.75a60.07 60.07 0 0115.797 2.101c.727.198 1.453-.342 1.453-1.096V18.75M3.75 4.5v.75A.75.75 0 013 6h-.75m0 0v-.375c0-.621.504-1.125 1.125-1.125H20.25M2.25 6v9m18-10.5v.75c0 .414.336.75.75.75h.75m-1.5-1.5h.375c.621 0 1.125.504 1.125 1.125v9.75c0 .621-.504 1.125-1.125 1.125h-.375m1.5-1.5H21a.75.75 0 00.75-.75v-.75m0 0H3.75m0 0h-.375a1.125 1.125 0 01-1.125-1.125V15m1.5 1.5v-.75A.75.75 0 003 15h-.75M15 10.5a3 3 0 11-6 0 3 3 0 016 0zm3 0h.008v.008H18V10.5zm-12 0h.008v.008H6V10.5z"/>
                  </svg>
                  <span class="text-xs font-medium text-blue-700 dark:text-blue-300">Your Owned Value</span>
                </div>
                <span class="text-sm font-bold text-blue-600 dark:text-blue-400">
                  Rs {{ formatNum(Math.round((form.current_price * form.ownership_percentage) / 100)) }}
                </span>
              </div>
              <p v-if="ownedValueInWords" class="text-xs text-blue-600 dark:text-blue-400 italic pl-6">
                {{ ownedValueInWords }}
              </p>
            </div>
          </div>
          <div>
            <label class="block text-sm font-semibold text-slate-700 dark:text-slate-300 mb-1.5">Purchase Date</label>

            <!-- Flatpickr for Desktop, Native for Mobile -->
            <input
              ref="datePickerInput"
              v-model="form.purchased_at"
              type="text"
              placeholder="Select date"
              class="hidden sm:block w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-xl px-4 py-2.5 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition cursor-pointer"
            />

            <!-- Mobile: Native Date Picker -->
            <input v-model="form.purchased_at" type="date"
              class="sm:hidden w-full bg-white dark:bg-slate-800 border border-slate-200 dark:border-slate-700
                     text-slate-800 dark:text-white placeholder-slate-400 rounded-xl px-4 py-2.5 text-sm
                     focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:border-transparent transition" />
          </div>
        </div>
        <div class="px-6 py-4 border-t border-slate-100 dark:border-slate-800 flex gap-3 rounded-b-2xl overflow-hidden">
          <button @click="closeModal"
            class="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition">
            Cancel
          </button>
          <button @click="submitForm"
            class="flex-1 bg-indigo-500 hover:bg-indigo-400 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm shadow-indigo-500/30 active:scale-95 transition">
            {{ editingAsset ? 'Update' : 'Add' }}
          </button>
        </div>
      </div>
    </div>

    <!-- Delete confirmation modal -->
    <div v-if="deleteModalOpen" @click="deleteModalOpen = false"
      class="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center p-4 z-50">
      <div @click.stop class="bg-white dark:bg-slate-900 rounded-2xl shadow-2xl w-full max-w-sm overflow-hidden">
        <div class="p-6">
          <div class="w-12 h-12 rounded-full bg-red-100 dark:bg-red-950/50 flex items-center justify-center mb-4">
            <svg class="w-6 h-6 text-red-600 dark:text-red-400" fill="none" viewBox="0 0 24 24" stroke="currentColor" stroke-width="2">
              <path stroke-linecap="round" stroke-linejoin="round" d="M12 9v3.75m-9.303 3.376c-.866 1.5.217 3.374 1.948 3.374h14.71c1.73 0 2.813-1.874 1.948-3.374L13.949 3.378c-.866-1.5-3.032-1.5-3.898 0L2.697 16.126zM12 15.75h.007v.008H12v-.008z"/>
            </svg>
          </div>
          <h3 class="text-lg font-bold text-slate-800 dark:text-white mb-2">Delete Asset?</h3>
          <p class="text-sm text-slate-600 dark:text-slate-400 mb-6">
            Are you sure you want to delete <strong>{{ assetToDelete?.asset_name }}</strong>? This action cannot be undone.
          </p>
          <div class="flex gap-3">
            <button @click="deleteModalOpen = false"
              class="flex-1 bg-slate-100 dark:bg-slate-800 text-slate-700 dark:text-slate-300 text-sm font-semibold px-4 py-2.5 rounded-xl hover:bg-slate-200 dark:hover:bg-slate-700 transition">
              Cancel
            </button>
            <button @click="deleteAsset"
              class="flex-1 bg-red-600 hover:bg-red-500 text-white text-sm font-semibold px-4 py-2.5 rounded-xl shadow-sm active:scale-95 transition">
              Delete
            </button>
          </div>
        </div>
      </div>
    </div>
  </AppLayout>
</template>

<script setup>
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue'
import { router } from '@inertiajs/vue3'
import AppLayout from '@/layouts/AppLayout.vue'
import flatpickr from 'flatpickr'
import 'flatpickr/dist/flatpickr.min.css'
import { ToWords } from 'to-words'

const toWords = new ToWords({ localeCode: 'en-IN' })

const assets = ref([])
const dataLoading = ref(true)
const search = ref('')
const errors = ref([])
const sortCol = ref('purchased_at')
const sortDir = ref('desc')
const expandedId = ref(null)
const menuOpenId = ref(null)
const modalOpen = ref(false)
const deleteModalOpen = ref(false)
const editingAsset = ref(null)
const assetToDelete = ref(null)
const form = ref({
  asset_name: '',
  purchased_price: '',
  current_price: '',
  ownership_percentage: 100,
  purchased_at: ''
})
const datePickerInput = ref(null)
let flatpickrInstance = null

const filtered = computed(() => {
  let result = assets.value
  if (search.value) {
    const s = search.value.toLowerCase()
    result = result.filter(a => a.asset_name.toLowerCase().includes(s))
  }
  return result
})

const sorted = computed(() => {
  const arr = [...filtered.value]
  arr.sort((a, b) => {
    let aVal = a[sortCol.value]
    let bVal = b[sortCol.value]

    if (sortCol.value === 'purchased_at') {
      aVal = aVal ? new Date(aVal) : new Date(0)
      bVal = bVal ? new Date(bVal) : new Date(0)
    }

    if (aVal < bVal) return sortDir.value === 'asc' ? -1 : 1
    if (aVal > bVal) return sortDir.value === 'asc' ? 1 : -1
    return 0
  })
  return arr
})

const totalValue = computed(() => assets.value.reduce((sum, a) => sum + a.current_price, 0))
const totalOwnedValue = computed(() => assets.value.reduce((sum, a) => sum + a.owned_value, 0))

function toggleSort(col) {
  if (sortCol.value === col) {
    sortDir.value = sortDir.value === 'asc' ? 'desc' : 'asc'
  } else {
    sortCol.value = col
    sortDir.value = 'desc'
  }
}

function toggleMenu(id) {
  menuOpenId.value = menuOpenId.value === id ? null : id
}

function openAdd() {
  editingAsset.value = null
  const today = new Date()
  const todayStr = `${today.getFullYear()}-${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`
  form.value = { asset_name: '', purchased_price: '', current_price: '', ownership_percentage: 100, purchased_at: todayStr }
  modalOpen.value = true
  nextTick(() => initFlatpickr())
}

function openEdit(asset) {
  editingAsset.value = asset
  form.value = {
    asset_name: asset.asset_name,
    purchased_price: asset.purchased_price,
    current_price: asset.current_price,
    ownership_percentage: asset.ownership_percentage,
    purchased_at: asset.purchased_at || ''
  }
  modalOpen.value = true
  menuOpenId.value = null
  nextTick(() => initFlatpickr())
}

function closeModal() {
  modalOpen.value = false
  editingAsset.value = null
  if (flatpickrInstance) {
    flatpickrInstance.destroy()
    flatpickrInstance = null
  }
}

function initFlatpickr() {
  if (datePickerInput.value && !flatpickrInstance) {
    flatpickrInstance = flatpickr(datePickerInput.value, {
      dateFormat: 'Y-m-d',
      defaultDate: form.value.purchased_at || new Date(),
      onChange: (selectedDates, dateStr) => {
        form.value.purchased_at = dateStr
      }
    })
  }
}

function submitForm() {
  if (editingAsset.value) {
    router.patch(`/wealth-assets/${editingAsset.value.id}`, { user_asset: form.value }, {
      onSuccess: () => {
        loadAssets()
        closeModal()
      }
    })
  } else {
    router.post('/wealth-assets', { user_asset: form.value }, {
      onSuccess: () => {
        loadAssets()
        closeModal()
      }
    })
  }
}

function confirmDelete(asset) {
  assetToDelete.value = asset
  deleteModalOpen.value = true
  menuOpenId.value = null
}

function deleteAsset() {
  if (assetToDelete.value) {
    router.delete(`/wealth-assets/${assetToDelete.value.id}`, {
      onSuccess: () => {
        loadAssets()
        deleteModalOpen.value = false
        assetToDelete.value = null
      }
    })
  }
}

function formatNum(n) {
  return new Intl.NumberFormat('en-PK').format(Math.round(n))
}

function formatDate(dateStr) {
  if (!dateStr) return ''
  const d = new Date(dateStr)
  return d.toLocaleDateString('en-PK', { year: 'numeric', month: 'short', day: 'numeric' })
}

const purchasedPriceInWords = computed(() => {
  const price = parseInt(form.value.purchased_price)
  if (!price || isNaN(price)) return ''
  try {
    return toWords.convert(price, { currency: true, ignoreDecimal: true })
  } catch (e) {
    return ''
  }
})

const currentPriceInWords = computed(() => {
  const price = parseInt(form.value.current_price)
  if (!price || isNaN(price)) return ''
  try {
    return toWords.convert(price, { currency: true, ignoreDecimal: true })
  } catch (e) {
    return ''
  }
})

const ownedValueInWords = computed(() => {
  const currentPrice = parseInt(form.value.current_price)
  const ownership = parseInt(form.value.ownership_percentage)
  if (!currentPrice || isNaN(currentPrice) || !ownership || isNaN(ownership)) return ''
  const ownedValue = Math.round((currentPrice * ownership) / 100)
  try {
    return toWords.convert(ownedValue, { currency: true, ignoreDecimal: true })
  } catch (e) {
    return ''
  }
})

async function loadAssets() {
  dataLoading.value = true
  try {
    const res = await fetch('/wealth-assets/data')
    const data = await res.json()
    assets.value = data.assets
  } catch (err) {
    console.error('Failed to load assets:', err)
  } finally {
    dataLoading.value = false
  }
}

function closeMenuOnClickOutside(e) {
  if (!e.target.closest('.relative')) {
    menuOpenId.value = null
  }
}

onMounted(async () => {
  document.addEventListener('click', closeMenuOnClickOutside)
  loadAssets()
})

onUnmounted(() => {
  document.removeEventListener('click', closeMenuOnClickOutside)
})
</script>

<style>
/* Flatpickr custom styles */
.flatpickr-calendar {
  border-radius: 12px;
  box-shadow: 0 10px 25px rgba(0, 0, 0, 0.1);
  border: 1px solid #e2e8f0;
}

.flatpickr-day.selected {
  background: #6366f1;
  border-color: #6366f1;
}

.flatpickr-day:hover {
  background: #eef2ff;
  border-color: #c7d2fe;
}

.flatpickr-months .flatpickr-month {
  background: #6366f1;
  color: white;
}

.flatpickr-current-month .flatpickr-monthDropdown-months {
  background: #6366f1;
  color: white;
}

.flatpickr-weekdays {
  background: #f8fafc;
}

/* Dark mode support */
@media (prefers-color-scheme: dark) {
  .flatpickr-calendar {
    background: #1e293b;
    border-color: #334155;
    box-shadow: 0 10px 25px rgba(0, 0, 0, 0.5);
  }

  .flatpickr-day {
    color: #e2e8f0;
  }

  .flatpickr-day:hover {
    background: #334155;
    border-color: #475569;
  }

  .flatpickr-weekdays {
    background: #0f172a;
  }

  .flatpickr-weekday {
    color: #94a3b8;
  }
}
</style>
